import { WorkforceInvite } from '../../types/workforce/workforceTypes'
import api from '../api'

export const inviteUser = async (userData: WorkforceInvite) => {
  const res = await api.post('/workforce/', {
    work_email: userData.work_email,
  })
  return res.data
}
export const fetchMe = async (fields?: string) => {
  const params = fields ? { fields } : {}
  const res = await api.get('/user/self/', { params })
  return res.data
}

export const setChatbotMode = async (chatbot_mode: 'user' | 'admin', idToken?: string) => {
  const res = await api.post(
    '/workforce/chatbot-mode/',
    { chatbot_mode },
    idToken
      ? {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      : undefined,
  )
  return res.data
}

export const fetchRoleCounts = async (): Promise<Record<string, number>> => {
  const res = await api.get('/workforce/role-counts/')
  return res.data
}

export const fetchEmployeeList = async ({
  search,
  page,
  pageSize,
  filters,
  has_uncompleted_path,
  ordering,
  fields,
  expand,
  minimal,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
  has_uncompleted_path?: boolean
  ordering?: string
  fields?: string
  expand?: string
  minimal?: boolean
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    has_uncompleted_path,
    // default fields include teams and manager info; callers can override via `fields` or request minimal
    fields:
      'teams,*,current_position.id,current_position.title,previous_position.id,previous_position.title,manager.first_name,manager.last_name,manager.id,profile_picture,primary_evaluator',
  }
  if (ordering) params.ordering = ordering
  // allow callers to override fields when provided
  if (fields) params.fields = fields
  // allow callers to request a minimal projection for small selects
  if (minimal) {
    params.fields = 'id,first_name,last_name,profile_picture'
  }
  // allow callers to opt out of default expands by passing expand: ''
  const defaultExpand = 'teams,current_position,previous_position,manager'
  if (expand === undefined) {
    params.expand = defaultExpand
  } else if (expand !== '') {
    params.expand = expand
  }

  Object.entries(filters).forEach(([key, values]) => {
    if (key === 'teams') {
      // Always use teams__in regardless of how many teams are selected
      params['teams__in'] = values.join(',')
    } else if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })
  const response = await api.get('/workforce/', { params })
  return response.data
}

/**
 * Fetch workforce roster (full org view without role-based filtering).
 * Use this for the workforce organization page so all employees are visible.
 */
export const fetchWorkforceRoster = async ({
  search,
  page,
  pageSize,
  filters,
  ordering,
  fields,
  expand,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
  ordering?: string
  fields?: string
  expand?: string
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    fields:
      fields ??
      'teams,*,current_position.id,current_position.title,previous_position.id,previous_position.title,manager.first_name,manager.last_name,manager.id,profile_picture,primary_evaluator',
  }
  if (ordering) params.ordering = ordering
  if (expand !== undefined && expand !== '') params.expand = expand

  Object.entries(filters).forEach(([key, values]) => {
    if (key === 'teams') {
      params['teams__in'] = values.join(',')
    } else if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })

  const response = await api.get('/workforce/roster/', { params })
  return response.data
}

export const exportEmployeeCompensationToXls = async ({ id }: { id: number | string }) => {
  const response = await api.get(`/workforce/${id}/export-compensation/`, {
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const url = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url

  const disposition = response.headers['content-disposition']
  const fileNameMatch = disposition?.match(/filename="(.+)"/)
  const fileName = fileNameMatch ? fileNameMatch[1] : `Compensation_${id}.xlsx`

  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const exportEmployeesToXls = async ({
  search,
  filters,
  ordering,
  ids,
}: {
  search?: string
  filters?: { [key: string]: string[] }
  ordering?: string
  ids?: (number | string)[]
}) => {
  const params: Record<string, unknown> = {}

  if (search) params.search = search
  if (ordering) params.ordering = ordering
  if (ids?.length) params.ids = ids.join(',')

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value?.length) params[key] = value.join(',')
    })
  }

  const response = await api.get('/workforce/export-xls/', {
    params,
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const url = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url

  const disposition = response.headers['content-disposition']
  let filename = 'employees.xlsx'

  if (disposition) {
    const match = disposition.match(/filename="(.+)"/)
    if (match?.[1]) filename = match[1]
  }

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()

  window.URL.revokeObjectURL(url)
}

export const exportEmployeesToCsv = async ({
  search,
  filters,
  ordering,
  ids,
}: {
  search?: string
  filters?: { [key: string]: string[] }
  ordering?: string
  ids?: (number | string)[]
}) => {
  const params: Record<string, any> = {}

  if (search) {
    params.search = search
  }

  if (ordering) {
    params.ordering = ordering
  }

  if (filters) {
    Object.entries(filters).forEach(([key, values]) => {
      if (key === 'teams') {
        params['teams__in'] = values.join(',')
      } else if (values.length > 1) {
        params[`${key}__in`] = values.join(',')
      } else if (values.length === 1) {
        params[key] = values[0]
      }
    })
  }

  if (ids && ids.length > 0) {
    params.ids = ids.join(',')
  }

  const response = await api.get('/workforce/export-csv/', {
    params,
    responseType: 'blob',
  })

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  const contentDisposition = response.headers['content-disposition']
  let filename = 'workforce_export.csv'
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1]
    }
  }

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const fetchShortEmployeeList = async ({
  search,
  page,
  pageSize,
}: {
  search: string
  page: number
  pageSize: number
}) => {
  const response = await api.get('/workforce/', {
    params: {
      search,
      page,
      page_size: pageSize,
      fields: 'id,first_name,last_name,work_email,office_phone_number,profile_picture',
    },
  })
  return response.data
}

export const updateEmployee = async (employee: any) => {
  const response = await api.patch(`/workforce/${employee.id}/`, employee)
  return response.data
}

export const completeProfile = async (employeeId: string, data: Record<string, unknown>) => {
  const response = await api.patch(`/workforce/${employeeId}/complete-profile/`, data)
  return response.data
}

export const fetchMyWorkforceProfile = async () => {
  const response = await api.get('/workforce/me/')
  return response.data
}

// invite employee
export const inviteEmployee = async (employee: any) => {
  const response = await api.patch(`/workforce/${employee.id}/invite/`)
  return response.data
}

export const addEmployee: (employee: unknown) => Promise<any> = async (employee: unknown) => {
  const response = await api.post('/workforce/', employee, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

export const fetchEmployee = async (
  employeeId: string,
  options?: { minimal?: boolean; fields?: string }
) => {
  const params: Record<string, any> = {}

  if (options?.fields) {
    params.fields = options.fields
  } else if (options?.minimal) {
    params.fields = 'id,phone_number,mfa_required,organization,language'
  } else {
    params.expand =
      'organization,teams,teams.team_manager,managed_teams,managed_teams.team_manager,' +
      'current_position,previous_position,primary_evaluator,secondary_evaluator,mentor'

    params.fields =
      'organization,teams,managed_teams,*,current_position,previous_position.id,' +
      'previous_position.title,manager.first_name,manager.last_name,manager.id,' +
      'primary_evaluator.id,primary_evaluator.first_name,primary_evaluator.last_name,primary_evaluator.profile_picture,' +
      'secondary_evaluator.id,secondary_evaluator.first_name,secondary_evaluator.last_name,secondary_evaluator.profile_picture,' +
      'mentor.id,mentor.first_name,mentor.last_name,mentor.profile_picture'
  }

  const response = await api.get(`/workforce/${employeeId}/`, { params })
  return response.data
}

export const disableEmployee = async (employeeId: string, deactivatedData: { [key: string]: string }) => {
  const response = await api.patch(`/workforce/${employeeId}/deactivate/`, { ...deactivatedData })
  return response.data
}

export const deleteEmployee = async (employeeId: string) => {
  const response = await api.delete(`/workforce/${employeeId}/`)
  return response.data
}

export const restoreEmployee = async (employeeId: string) => {
  const response = await api.patch(`/workforce/${employeeId}/restore/`)
  return response.data
}

export const fetchTeamList = async ({
  search,
  page,
  pageSize,
  filters,
  fields,
  expand,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
  fields?: string
  expand?: string
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    filters: filters,
  }
  // Default to only expanding manager (avoid expanding members by default to reduce payload)
  const defaultExpand = 'team_manager'
  if (expand === undefined) {
    params.expand = defaultExpand
  } else if (expand !== '') {
    // allow callers to pass an empty string to explicitly request no expand
    params.expand = expand
  }
  // Default to minimal useful fields (avoid '*' which pulls created_at/updated_at/description)
  params.fields =
    fields ??
    'id,name,country,members,team_manager.id,team_manager.first_name,team_manager.last_name,team_manager.profile_picture,team_manager.work_email'
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })
  const response = await api.get('/workforce/team/', { params })
  return response.data
}

export const fetchTeam = async (
  teamId: string,
  filters?: { [key: string]: string[] },
  search?: string,
  fields?: string,
) => {
  const params: Record<string, any> = {
    search,
    expand: 'members,team_manager,members.current_position,team_manager.current_position',
    fields:
      fields ??
      'id,name,country,team_manager.id,team_manager.first_name,team_manager.last_name,team_manager.profile_picture,team_manager.country_of_work,team_manager.current_position.id,team_manager.current_position.title,members.id,members.first_name,members.last_name,members.profile_picture,members.country_of_work,members.current_position.id,members.current_position.title',
  }
  if (filters) {
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 1) {
        params[`${key}__in`] = values.join(',')
      } else if (values.length === 1) {
        params[key] = values[0]
      }
    })
  }
  const response = await api.get(`/workforce/team/${teamId}/`, { params })
  return response.data
}

export const addTeam = async (team: unknown) => {
  const response = await api.post('/workforce/team/', team)
  return response.data
}

export const updateTeam = async (team: any) => {
  const response = await api.patch(`/workforce/team/${team.id}/`, team)
  return response.data
}

export const addTeamMembers = async (teamId: string, employeeIds: string[]) => {
  const response = await api.patch(`/workforce/team/${teamId}/add_members/`, { employees: employeeIds })
  return response.data
}

export const removeTeamMembers = async (teamId: string, employeeIds: string[]) => {
  const response = await api.patch(`/workforce/team/${teamId}/remove_members/`, { employees: employeeIds })
  return response.data
}

export const deleteTeam = async (teamId: string) => {
  const response = await api.delete(`/workforce/team/${teamId}/`)
  return response.data
}

export async function fetchOrganization(id: string) {
  const response = await fetch(`/api/organizations/${id}`)
  if (!response.ok) throw new Error('Failed to fetch organization')
  return response.json()
}

export const fetchAnniversariesThisWeek = async (week: string, page = 1, pageSize = 5) => {
  const response = await api.get('workforce/anniversaries/', {
    params: { week, page, page_size: pageSize },
  })
  return response.data
}

/** Start of current month and end of next month (YYYY-MM-DD) for expiring filters */
function getExpiringDateRange() {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const startThisMonth = fmt(new Date(now.getFullYear(), now.getMonth(), 1))
  const endNextMonth = fmt(new Date(now.getFullYear(), now.getMonth() + 2, 0))
  return { startThisMonth, endNextMonth }
}

/**
 * Fetches employees whose temporary_contract_end_date or probation_end_date falls in this month or next month,
 * using the workforce list endpoint with date filters. Merges and dedupes by employee id.
 */
export const fetchExpiringContractsAndProbations = async (page = 1, pageSize = 10) => {
  const { startThisMonth, endNextMonth } = getExpiringDateRange()
  const filtersContract = {
    temporary_contract_end_date__gte: [startThisMonth],
    temporary_contract_end_date__lte: [endNextMonth],
  }
  const filtersProbation = {
    probation_end_date__gte: [startThisMonth],
    probation_end_date__lte: [endNextMonth],
  }
  const fields = 'id,first_name,last_name,profile_picture,temporary_contract_end_date,probation_end_date'
  const base = { search: '', page, pageSize, filters: {} as { [key: string]: string[] } }

  const [contractRes, probationRes] = await Promise.all([
    fetchEmployeeList({ ...base, filters: filtersContract, fields, expand: '' }),
    fetchEmployeeList({ ...base, filters: filtersProbation, fields, expand: '' }),
  ])

  const byId = new Map<number, any>()
  for (const e of contractRes?.data ?? []) {
    byId.set(e.id, { ...e })
  }
  for (const e of probationRes?.data ?? []) {
    if (byId.has(e.id)) {
      const existing = byId.get(e.id)
      byId.set(e.id, { ...existing, probation_end_date: e.probation_end_date })
    } else {
      byId.set(e.id, { ...e })
    }
  }
  const data = Array.from(byId.values())
  const has_next = !!(contractRes?.has_next || probationRes?.has_next)
  return { data, has_next, max_page: Math.max(contractRes?.max_page ?? 1, probationRes?.max_page ?? 1) }
}

export type BenefitFrequency = 'single' | 'monthly' | 'annual'

export type BenefitType =
  | 'pension'
  | 'health'
  | 'education'
  | 'vehicle'
  | 'travel_allowance'
  | 'extra_month_salary'
  | 'vacation_allowance'
  | 'other'

export interface Benefit {
  id: string
  organization: number
  employee: number | null
  employee_name?: string
  employee_profile_picture?: string
  team: number | null
  team_name?: string
  country_of_work: string | null

  frequency: BenefitFrequency
  type: BenefitType
  description: string

  value: string
  currency: string
  benefit_date: string

  amount_eur: string
  added_by: number | null
  added_by_name?: string
  added_by_profile_picture?: string

  created_at: string
  updated_at: string
}

export interface BenefitChangeLog {
  id: string
  benefit: string | {
    id: string
    type?: string
    value?: string
    currency?: string
    frequency?: string
    description?: string
    employee?: {
      id: string
      first_name: string
      last_name: string
      profile_picture?: string
    } | null
    team?: {
      id: string
      name: string
    } | null
    country_of_work?: string | null
  }
  action: 'create' | 'update' | 'delete'
  changed_by: string | {
    id: string
    first_name: string
    last_name: string
    profile_picture?: string
  }
  field_name?: string
  previous_value: string | any | null
  new_value: string | any | null
  created_at: string
}

// ---------- Admin (Payroll -> Benefits) ----------

// Fetch benefits - admin sees all/filtered, user sees only their benefits
export async function fetchBenefits(params?: {
  page?: number
  page_size?: number
  search?: string
  employee?: string
  team?: string
  type?: string
  frequency?: string
  currency?: string
}) {
  const res = await api.get('/workforce/benefits/', { 
    params: {
      ...params,
      expand: 'employee,team,added_by',
      fields: '*,employee.id,employee.first_name,employee.last_name,employee.profile_picture,team.id,team.name,added_by.id,added_by.first_name,added_by.last_name,added_by.profile_picture',
    }
  })
  return res.data
}

// Fetch single benefit detail
export async function fetchBenefit(id: string) {
  const res = await api.get(`/workforce/benefits/${id}/`)
  return res.data
}

// Create benefit (admin only)
export async function createBenefit(data: unknown) {
  const res = await api.post('/workforce/benefits/', data)
  return res.data
}

// Update benefit (admin only)
export async function updateBenefit(id: string, data: unknown) {
  const res = await api.patch(`/workforce/benefits/${id}/`, data)
  return res.data
}

// Delete benefit
export async function deleteBenefit(id: string) {
  await api.delete(`/workforce/benefits/${id}/`)
}

// Fetch benefit change logs (admin only)
export async function fetchBenefitChangeLogs(params?: {
  page?: number
  page_size?: number
  search?: string
  employee?: string
  changed_by?: string
}) {
  const res = await api.get('/workforce/benefits/change-logs/', { 
    params: {
      ...params,
      expand: 'changed_by,benefit.employee,benefit.team,benefit.country_of_work'
    }
  })
  return res.data
}

// Legacy function for backward compatibility - now uses new endpoint
export async function fetchEmployeeBenefits(employeeId: number) {
  const res = await api.get('/workforce/benefits/', {
    params: { employee: employeeId.toString() }
  })
  return res.data
}

export const exportBenefitsToXls = async ({
  search,
  filters,
  ids,
}: {
  search?: string
  filters?: { [key: string]: string[] }
  ids?: (number | string)[]
}) => {
  const params: Record<string, any> = {}

  if (search) params.search = search
  if (ids?.length) params.ids = ids.join(',')

  if (filters) {
    Object.entries(filters).forEach(([key, values]) => {
      if (values?.length > 1) {
        params[`${key}__in`] = values.join(',')
      } else if (values?.length === 1) {
        params[key] = values[0]
      }
    })
  }

  const response = await api.get('/workforce/benefits/export-xls/', {
    params,
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  const disposition = response.headers['content-disposition']
  let filename = 'benefits.xlsx'
  const match = disposition?.match(/filename="(.+)"/)
  if (match?.[1]) filename = match[1]

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const exportBenefitsToCsv = async ({
  search,
  filters,
  ids,
}: {
  search?: string
  filters?: { [key: string]: string[] }
  ids?: (number | string)[]
}) => {
  const params: Record<string, any> = {}

  if (search) params.search = search
  if (ids?.length) params.ids = ids.join(',')

  if (filters) {
    Object.entries(filters).forEach(([key, values]) => {
      if (values?.length > 1) {
        params[`${key}__in`] = values.join(',')
      } else if (values?.length === 1) {
        params[key] = values[0]
      }
    })
  }

  const response = await api.get('/workforce/benefits/export-csv/', {
    params,
    responseType: 'blob',
  })

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  const disposition = response.headers['content-disposition']
  let filename = 'benefits.csv'
  const match = disposition?.match(/filename="?([^"]+)"?/)
  if (match?.[1]) filename = match[1]

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}



export const exportBenefitChangeLogsToXls = async ({
  search,
  employee,
  changed_by,
}: {
  search?: string
  employee?: string
  changed_by?: string
}) => {
  const response = await api.get('/workforce/benefits/change-logs/export-xls/', {
    params: {
      ...(search ? { search } : {}),
      ...(employee ? { employee } : {}),
      ...(changed_by ? { changed_by } : {}),
    },
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  const disposition = response.headers['content-disposition']
  let filename = 'benefit_change_logs.xlsx'
  const match = disposition?.match(/filename="(.+)"/)
  if (match?.[1]) filename = match[1]

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const exportBenefitChangeLogsToCsv = async ({
  search,
  employee,
  changed_by,
}: {
  search?: string
  employee?: string
  changed_by?: string
}) => {
  const response = await api.get('/workforce/benefits/change-logs/export-csv/', {
    params: {
      ...(search ? { search } : {}),
      ...(employee ? { employee } : {}),
      ...(changed_by ? { changed_by } : {}),
    },
    responseType: 'blob',
  })

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  const disposition = response.headers['content-disposition']
  let filename = 'benefit_change_logs.csv'
  const match = disposition?.match(/filename="?([^"]+)"?/)
  if (match?.[1]) filename = match[1]

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
